import { FC, ReactElement, ReactNode, useState } from 'react';
import { AssetType } from '../../../../utils';
import {
  AcceptOfferNonComplexProductsTerms,
  AcceptOfferNonComplexProductsTermsTranslations,
} from './components/AcceptOfferNonComplexProductsTerms';
import {
  AcceptOfferComplexProductsTerms,
  AcceptOfferComplexProductsTermsTranslations,
} from './components/AcceptOfferComplexProductsTerms';
import { useClientUserInfo } from '../../../../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import { LegalDocumentsTermLabelTranslations } from './components/LegalDocumentsTermLabel';
import { TradeOfferDataNonNullable } from '../../../types';
import { AcceptOfferStockEtfEtcEligibleTerms } from './components/AcceptOfferStockEtfEtcEligibleTerms';
import { AcceptOfferStockEtfEtcNonEligibleTerms } from './components/AcceptOfferStockEtfEtcNonEligibleTerms';

export type AcceptBuyTradeDisclaimerWrapperProps = {
  translations: LegalDocumentsTermLabelTranslations &
    AcceptOfferNonComplexProductsTermsTranslations &
    AcceptOfferComplexProductsTermsTranslations & {
      investingRiskToggle: string;
      stocksToggleNotAppropriate: string;
    };
  variant: 'buy' | 'sell' | 'swap';
  legalUrl: string;
  assetType: AssetType;
  warnings: TradeOfferDataNonNullable['warnings'];
  kid?: string;
  children?: (props: { areTermsAgreedTo: boolean }) => ReactNode;
};

export const AcceptBuyTradeDisclaimerWrapper: FC<
  AcceptBuyTradeDisclaimerWrapperProps
> = ({
  translations,
  variant,
  children,
  legalUrl,
  assetType,
  warnings,
  kid,
}) => {
  const isCrypto = assetType === 'CryptoAsset';

  const isMetalAsset = assetType === 'MetalAsset';

  const isNotCryptoOrMetal = !isCrypto && !isMetalAsset;

  const isNonComplexEquityAsset = [
    AssetType.EQUITY_ETC,
    AssetType.EQUITY_ETF,
    AssetType.EQUITY_STOCK,
  ].includes(assetType);

  const isComplexEquityAsset = [
    AssetType.EQUITY_COMPLEX_ETF,
    AssetType.EQUITY_COMPLEX_ETC,
  ].includes(assetType);

  const userData = useClientUserInfo();

  const requiresRiskWarningAcknowledgement =
    isComplexEquityAsset &&
    userData.appropriatenessComplexProducts?.status === 'NOT_APPROPRIATE';

  const noVerificationRequired =
    isNonComplexEquityAsset ||
    (isComplexEquityAsset && !requiresRiskWarningAcknowledgement) ||
    isMetalAsset ||
    isCrypto ||
    variant === 'sell';

  const [disclaimerAgreed, setDisclaimerAgreed] = useState(
    noVerificationRequired ? true : false,
  );

  const toggleDisclaimerAgreement = () => {
    setDisclaimerAgreed((state) => !state);
  };

  const isUserAppropriate =
    userData &&
    userData.appropriateness !== 'NOT_APPROPRIATE' &&
    userData.appropriatenessRequiresTestCompletion === false &&
    userData.appropriatenessRequiresTestResubmission === false;

  let AcceptOfferTermsComponent: ReactElement | null = null;

  if (isNonComplexEquityAsset) {
    AcceptOfferTermsComponent = (
      <AcceptOfferNonComplexProductsTerms
        translations={translations}
        legalDocumentUrl={legalUrl}
        warnings={warnings}
        kid={kid}
      />
    );
  } else if (isComplexEquityAsset) {
    AcceptOfferTermsComponent = (
      <AcceptOfferComplexProductsTerms
        complexAppropriatenessStatus={
          userData.appropriatenessComplexProducts?.status ?? 'UNKNOWN'
        }
        translations={translations}
        legalDocumentUrl={legalUrl}
        warnings={warnings}
        termsAgreed={disclaimerAgreed}
        setTermsAgreed={toggleDisclaimerAgreement}
      />
    );
  } else if (
    isNotCryptoOrMetal &&
    isUserAppropriate &&
    !noVerificationRequired
  ) {
    AcceptOfferTermsComponent = (
      <AcceptOfferStockEtfEtcEligibleTerms
        legalDocumentUrl={legalUrl}
        translations={translations}
        setTermsAgreed={toggleDisclaimerAgreement}
        termsAgreed={disclaimerAgreed}
      />
    );
  } else if (
    isNotCryptoOrMetal &&
    !isUserAppropriate &&
    !noVerificationRequired
  ) {
    AcceptOfferTermsComponent = (
      <AcceptOfferStockEtfEtcNonEligibleTerms
        legalDocumentUrl={legalUrl}
        translations={translations}
        setTermsAgreed={setDisclaimerAgreed}
      />
    );
  }

  return (
    <>
      {AcceptOfferTermsComponent}

      {children &&
        children({
          areTermsAgreedTo: disclaimerAgreed,
        })}
    </>
  );
};
